/**
 * @file 回答区知识库引用卡片组件（KnowledgeCitation）
 * 使用 database_query 协议展示知识库引用
 */
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { isEmpty, isArray } from 'lodash-es';
import classNames from 'classnames';
import BasicImage from '@home/components/BasicImage';
import SvgIcon from '@home/components/SvgIcon';
import { Collapse } from '@/pages/aigc/components/Collapse';
import EllipsisHover from '@/pages/aigc/components/EllipsisHover';
import {
  getChannelIcon,
  KB_TEXT_ICON
} from '@/pages/aigc/pages/Agent/components/QAInput/KnowledgeBaseMention/utils/channelIconMap';
import {
  openDocUrl,
  normalizeDatabaseQuery,
  type INormalizedDoc,
  type INormalizedGroup,
} from './utils';
import type { IDatabaseQueryGroup } from '../../types/history';
import './index.scss';

/**
 * KnowledgeCitation 组件属性
 * @interface IKnowledgeCitationProps
 */
interface IKnowledgeCitationProps {
  /** database_query 的 value */
  value?: IDatabaseQueryGroup[];
  /** 数据类型标识 */
  type?: 'database_query' | string;
  /** 消息 ID（预留） */
  messageId?: string;
}

const PREFIX = 'dba-kb-citation';

/**
 * 回答区知识库引用卡片组件
 * 使用 database_query 协议展示知识库引用
 * @param props - 组件属性
 * @param props.value - database_query 数据
 */
const KnowledgeCitation: React.FC<IKnowledgeCitationProps> = ({
  value,
}) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const groups = useMemo(() => {
    if (!isArray(value)) return [];
    return normalizeDatabaseQuery(value);
  }, [value]);

  const totalDocCount = useMemo(
    () => groups.reduce((sum, group) => sum + group.documents.length, 0),
    [groups],
  );

  const subtitleText = useMemo(() => {
    const kbNames = groups.map((g) => g.dbName).filter(Boolean);
    if (kbNames.length === 0) return '';
    const namesPart = kbNames.map((name) => `【${name}】`).join('');
    return formatMessage(
      { id: 'dba_kb_citation_subtitle' },
      { names: namesPart },
    );
  }, [groups, formatMessage]);

  /**
   * 点击文档项时跳转文档链接
   * @param doc - 文档数据
   */
  const handleDocClick = useCallback((doc: INormalizedDoc) => {
    openDocUrl(doc.docUrl);
  }, []);

  /**
   * 切换折叠/展开卡片
   */
  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  /**
   * 渲染片段数量标签（无浮窗）
   * @param doc - 文档数据
   */
  const renderChunkTag = (doc: INormalizedDoc) => {
    const chunkLabel = formatMessage(
      { id: 'dba_kb_citation_chunk_count' },
      { count: doc.chunkCount },
    );
    return (
      <span className={`${PREFIX}__doc-chunk`}>
        {chunkLabel}
      </span>
    );
  };

  if (isEmpty(groups) || totalDocCount === 0) {
    return null;
  }

  /**
   * 渲染单个文档项（图标 + 名称 + 片段数量）
   * @param doc - 文档数据
   * @param index - 文档在分组中的索引
   */
  const renderDocItem = (doc: INormalizedDoc, index: number) => {
    const icon = doc.docIcon || getChannelIcon(doc.channel, doc.docType);
    const clickable = Boolean(doc.docUrl);

    return (
      <div
        key={`${doc.docName}-${index}`}
        className={classNames(`${PREFIX}__doc`, {
          [`${PREFIX}__doc--clickable`]: clickable,
        })}
        onClick={() => {
          if (!clickable) return;
          handleDocClick(doc);
        }}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={(e) => {
          if (!clickable) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleDocClick(doc);
          }
        }}
      >
        <BasicImage
          src={icon}
          alt={doc.docName}
          errorSrc={getChannelIcon()}
          className={`${PREFIX}__doc-icon`}
        />
        <div className={`${PREFIX}__doc-body`}>
          <EllipsisHover
            text={doc.docName}
            className={`${PREFIX}__doc-name`}
            overlayClassName="dba-nav-menu-overlay dba-kb-citation-name-bubble"
            placement="top"
          />
          {doc.chunkCount > 0 && renderChunkTag(doc)}
        </div>
      </div>
    );
  };

  /**
   * 渲染单个知识库分组
   * @param group - 分组数据
   * @param index - 分组索引
   */
  const renderGroup = (group: INormalizedGroup, index: number) => (
    <div key={`${group.dbName}-${index}`} className={`${PREFIX}__group`}>
      {group.dbName && (
        <div className={`${PREFIX}__group-title`}>{group.dbName}</div>
      )}
      <div className={`${PREFIX}__group-docs`}>
        {group.documents.map(renderDocItem)}
      </div>
    </div>
  );

  return (
    <div className={`${PREFIX}-card`}>
      <div
        className={`${PREFIX}__header`}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <div className={`${PREFIX}__header-main`}>
          <BasicImage
            src={KB_TEXT_ICON}
            alt=""
            className={`${PREFIX}__title-icon`}
          />
          <span className={`${PREFIX}__title-text`}>
            {formatMessage({ id: 'dba_kb_citation_title' })}
          </span>
          <span className={`${PREFIX}__title-count`}>
            {totalDocCount}
            {formatMessage({ id: 'dba_kb_citation_docs_unit' })}
          </span>
          {subtitleText && (
            <span className={`${PREFIX}__subtitle`}>{subtitleText}</span>
          )}
        </div>
        <span className={`${PREFIX}__toggle`}>
          <div className="dba-thinking-process">
            <SvgIcon type={isOpen ? 'point-top' : 'point-down'}></SvgIcon>
          </div>
        </span>
      </div>

      <Collapse
        className={classNames(`${PREFIX}__collapse`, 'collapse-css-transition')}
        isOpen={isOpen}
        overflowOnExpanded
      >
        <div className={`${PREFIX}__content`}>
          {groups.map(renderGroup)}
        </div>
      </Collapse>
    </div>
  );
};

export default memo(KnowledgeCitation);
